import { useEffect, useState } from "react";

export const RECENT_VIEWED_STORAGE_KEY = "mj-recent-viewed-activities";
export const RECENT_VIEWED_CHANGED_EVENT = "mj-recent-viewed-changed";
const MAX_RECENT_VIEWED = 12;

export interface RecentViewedItem {
  slug: string;
  title: string;
  city: string;
  price: number;
  currency: string;
  priceType?: string | number;
  image?: string;
  category?: string;
  /**
   * The language the stored title, city and category are written in. The
   * rail shows them only on a page in that same language; anywhere else it
   * waits for the activity fetched in the page's language. Entries saved
   * before this field existed have no language and are never trusted.
   */
  lang?: string;
  viewedAt: number;
}

/**
 * The text to show for one rail entry: the activity fetched in the page's
 * language when it has arrived, else the stored copy only when it was saved
 * in this language. `null` means "not ready, render nothing yet", so a
 * Dutch category or an Arabic title never flashes on an Italian page.
 */
export function recentViewedDisplay(
  item: RecentViewedItem,
  pageLang: string,
  fresh:
    | { title?: string; city?: { name?: string } | null; category?: { name?: string } | null }
    | undefined,
): { title: string; city: string; category: string | undefined } | null {
  if (fresh?.title) {
    return {
      title: fresh.title,
      city: fresh.city?.name ?? "",
      // A missing category on the fresh row is no excuse to show the stored,
      // possibly other-language one.
      category: fresh.category?.name || undefined,
    };
  }
  if (item.lang && item.lang === pageLang) {
    return { title: item.title, city: item.city, category: item.category };
  }
  return null;
}

function isRecentViewedItem(x: unknown): x is RecentViewedItem {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.slug === "string" &&
    typeof o.title === "string" &&
    typeof o.city === "string" &&
    typeof o.price === "number" &&
    typeof o.currency === "string" &&
    typeof o.viewedAt === "number"
  );
}

export function readRecentViewed(): RecentViewedItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_VIEWED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentViewedItem).sort((a, b) => b.viewedAt - a.viewedAt);
  } catch {
    return [];
  }
}

export function writeRecentViewed(items: RecentViewedItem[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RECENT_VIEWED_STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(RECENT_VIEWED_CHANGED_EVENT));
  } catch {
    // Ignore private-mode quota and serialization failures.
  }
}

export function addRecentViewed(item: Omit<RecentViewedItem, "viewedAt">): void {
  const next = [
    { ...item, viewedAt: Date.now() },
    ...readRecentViewed().filter((existing) => existing.slug !== item.slug),
  ].slice(0, MAX_RECENT_VIEWED);
  writeRecentViewed(next);
}

/** Forget a slug, e.g. once the API reports the listing no longer exists. */
export function removeRecentViewed(slug: string): void {
  const current = readRecentViewed();
  const next = current.filter((existing) => existing.slug !== slug);
  if (next.length !== current.length) writeRecentViewed(next);
}

export function useRecentViewed(): RecentViewedItem[] {
  const [items, setItems] = useState<RecentViewedItem[]>([]);

  useEffect(() => {
    const sync = () => setItems(readRecentViewed());
    sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === RECENT_VIEWED_STORAGE_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(RECENT_VIEWED_CHANGED_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(RECENT_VIEWED_CHANGED_EVENT, sync as EventListener);
    };
  }, []);

  return items;
}
